import React, { lazy, useState, Suspense, useEffect } from 'react'
import createStore from 'teaful'
import { Affix, Menu, Button, Dropdown } from 'antd'
import { ContextToolbar } from '@guillotinaweb/react-gmi'
import { PanelPermissions } from '@guillotinaweb/react-gmi'
import { useTraversal, useLocation } from '@guillotinaweb/react-gmi'

import {
  FileImageOutlined,
  MoreOutlined,
  VideoCameraOutlined,
  DownOutlined,
  EyeOutlined,
} from '@ant-design/icons'

import ProductOptions from './partials/options'
import useSaveProduct from './useSaveProduct'
import { AuditLogTab } from '../../../common/auditlog'
import { ContentReviewTab } from '../../../components/content-review-tab'
import { PriceComparatorTab } from '../../../components/price-comparator-tab'
import { PackEditor } from './pack_editor'
import { SEOTab } from '../../../components/seo-tab'
import { SITES, SITES_INFO } from '../../../consts'
import { TabsPanelWithLock } from '../../../components/tab-panel-with-lock'
import { getDomain, getProductMask } from '../../../utils'

import './styles.css'

export const { useStore, getStore } = createStore({})

const QUERY_PARAM = 'menu'
const loadComponent = (key) =>
  lazy(() =>
    import(`./partials/${key}`).catch(() => ({ default: () => 'No content' }))
  )

function ProductEditor() {
  const [location, setLocation] = useLocation()
  const defaultItem = location.get('tab') === 'Images' ? 'images' : 'details'
  const item = location.get(QUERY_PARAM) || defaultItem
  const ctx = useTraversal()
  const product = ctx.state.context
  const [Comp, setComp] = useState()
  const [key, setKey] = useState(item)
  const [saving, save] = useSaveProduct()

  // Initialize
  useStore.region(product.region)
  useStore.tags_by_category(product.tags_by_category)
  useStore.otras_props(product.otras_props)
  useStore.new_otras_props(product.new_otras_props)

  function onClick(e) {
    setLocation({ [QUERY_PARAM]: e.key })
    setComp(loadComponent(e.key))
    setKey(e.key)
  }

  function onSave() {
    const [store] = getStore()
    save(store)
  }

  useEffect(() => {
    const [, setStore] = getStore()
    setStore({})
    setComp(loadComponent(item))
  }, [])

  const menu = (
    <Menu>
      {SITES.map((site) => {
        const data = { id: product['@name'], type: product.tipo }
        const lang = SITES_INFO[site].lang
        const url = `https://${getDomain(site)}${getProductMask(data, lang)}`
        return (
          <Menu.Item key={site} onClick={() => window.open(url, '_blank')}>
            {site}
          </Menu.Item>
        )
      })}
    </Menu>
  )

  return (
    <div className="product-editor" key={key}>
      <Affix>
        <div
          style={{
            backgroundColor: 'white',
            display: 'flex',
            padding: 10,
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <h1 className="title is-size-4 is-primary">{product.nombre}</h1>
          <div>
            <Dropdown overlay={menu}>
              <Button>
                <EyeOutlined /> View product on <DownOutlined />
              </Button>
            </Dropdown>
            <Button type="primary" disabled={saving} onClick={onSave}>
              {saving ? 'Saving...' : 'Save product'}
            </Button>
          </div>
        </div>
      </Affix>
      <div style={{ display: 'flex' }}>
        <Menu
          onClick={onClick}
          style={{ width: 256 }}
          defaultSelectedKeys={[item]}
          mode="inline"
        >
          <Menu.ItemGroup key="g0" title="Name, lema, region, category...">
            <Menu.Item key="details" icon={<i className="icon icon-bottle" />}>
              Product details
            </Menu.Item>
          </Menu.ItemGroup>

          <Menu.ItemGroup key="g1" title="Product page sections">
            <Menu.Item
              key="description"
              icon={<i className="icon icon-wine" />}
            >
              El vino (description)
            </Menu.Item>
            <Menu.Item
              key="a_que_sabe"
              icon={<i className="icon icon-taste" />}
            >
              ¿A qué sabe este vino?
            </Menu.Item>
            <Menu.Item
              key="como_beber"
              icon={<i className="icon icon-service" />}
            >
              Consumo y conservación
            </Menu.Item>
            <Menu.Item
              key="maridajes"
              icon={<i className="icon icon-marriage" />}
            >
              Maridajes
            </Menu.Item>
            <Menu.Item key="score" icon={<i className="icon icon-score" />}>
              Premios y puntuaciones
            </Menu.Item>
            <Menu.Item
              key="elaboration"
              icon={<i className="icon icon-elaboration" />}
            >
              Elaboración
            </Menu.Item>
            <Menu.Item
              key="vineyard"
              icon={<i className="icon icon-vineyard" />}
            >
              Viñedo
            </Menu.Item>
            <Menu.Item
              key="experts_rating"
              icon={<i className="icon icon-opinions" />}
            >
              Experts rating
            </Menu.Item>
          </Menu.ItemGroup>

          <Menu.ItemGroup key="g2" title="More">
            <Menu.Item key="images" icon={<FileImageOutlined />}>
              Product images
            </Menu.Item>
            <Menu.Item key="video" icon={<VideoCameraOutlined />}>
              Product video
            </Menu.Item>
            <Menu.Item key="options" icon={<MoreOutlined />}>
              Options
            </Menu.Item>
          </Menu.ItemGroup>
        </Menu>
        <div style={{ margin: 20, width: '100%', overflowX: 'auto' }}>
          {Comp && (
            <Suspense fallback={<div />}>
              <Comp product={product} />
            </Suspense>
          )}
        </div>
      </div>
    </div>
  )
}

function Complements() {
  const ctx = useTraversal()
  const complement = ctx.state.context
  const ref = complement.opciones?.[0]?.referencia
  return (
    <div>
      For now use the{' '}
      {ref ? (
        <a
          rel="noopener noreferrer"
          target="_blank"
          href={`/administracion/contenidos/edicion_complemento.html?id_complemento=${ref}`}
        >
          old backoffice page
        </a>
      ) : (
        'old backoffice page'
      )}{' '}
      for complements.
    </div>
  )
}

export function ProductView(props) {
  const isPack = props.state.path.startsWith('/packs')
  const isComplement = props.state.path.startsWith('/complementos')
  const ctx = useTraversal()
  const Options = () => <ProductOptions product={ctx.state.context} />
  const editors = ['Product', 'Pack', 'Complements']
  let tab = editors[0]
  if (isPack) tab = editors[1]
  if (isComplement) tab = editors[2]

  let tabs = {
    Product: ProductEditor,
    Permissions: PanelPermissions,
    AuditLog: AuditLogTab,
    SEO: SEOTab,
    ContentReview: ContentReviewTab,
    'Price comparator': PriceComparatorTab,
  }
  const permissions = {
    [tab]: 'guillotina.ModifyContent',
    Permissions: 'guillotina.SeePermissions',
    SEO: 'guillotina.ModifyContent',
    ContentReview: 'guillotina.ModifyContent',
    'Price comparator': 'guillotina.ModifyContent',
  }

  if (isPack) {
    delete tabs.Product
    tabs = { Pack: PackEditor, Options, ...tabs }
  }

  if (isComplement) {
    delete tabs.Product
    tabs = { Complements, Options, ...tabs }
  }

  const calculated = ctx.filterTabs(tabs, permissions)

  return (
    <TabsPanelWithLock
      tabs={calculated}
      currentTab={tab}
      editableTabs={editors}
      fallback={ProductEditor}
      rightToolbar={<ContextToolbar {...props} />}
      {...props}
    />
  )
}
